import { supabase } from '../lib/supabase';

export interface Video {
  id: string;
  org_id: string;
  storage_path: string;
  public_url: string | null;
  source_type: 'youtube' | 'supabase_storage' | 'cloudflare_r2' | 'local';
  file_name: string;
  description: string | null;
  size_bytes: number | null;
  duration_seconds: number | null;
  width: number | null;
  height: number | null;
  codec: string | null;
  bitrate: number | null;
  status: 'pending_upload' | 'uploaded' | 'processing' | 'ready' | 'error';
  processing_notes: Record<string, any> | null;
  uploaded_by: string;
  uploaded_at: string;
  created_at: string;
  updated_at: string;
  is_active: boolean;
}

export interface CreateVideoInput {
  org_id: string;
  storage_path: string;
  source_type: 'supabase_storage' | 'cloudflare_r2' | 'local';
  file_name: string;
  description?: string | null;
  size_bytes?: number;
  uploaded_by: string;
}

export interface UpdateVideoInput {
  status?: Video['status'];
  file_name?: string;
  description?: string | null;
  is_active?: boolean;
  size_bytes?: number;
  duration_seconds?: number;
  width?: number;
  height?: number;
  codec?: string;
  bitrate?: number;
  processing_notes?: Record<string, any>;
  public_url?: string;
}

export interface ServiceResult<T> {
  success: boolean;
  data?: T;
  error?: string;
}

/**
 * Service for managing videos table operations
 */
export const videosService = {
  /**
   * Get all videos for an organization
   */
  getVideosByOrg: async (orgId: string): Promise<ServiceResult<Video[]>> => {
    try {
      const { data, error } = await supabase
        .from('videos')
        .select('*')
        .eq('org_id', orgId)
        .eq('is_active', true);

      if (error) {
        console.error('Error fetching videos:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: data || [] };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Error in getVideosByOrg:', errorMessage);
      return { success: false, error: errorMessage };
    }
  },

  /**
   * Get a single video by ID
   */
  getVideoById: async (videoId: string): Promise<ServiceResult<Video>> => {
    try {
      const { data, error } = await supabase
        .from('videos')
        .select('*')
        .eq('id', videoId)
        .eq('is_active', true)
        .single();

      if (error) {
        console.error('Error fetching video:', error);
        return { success: false, error: error.message };
      }

      if (!data) {
        return { success: false, error: 'Video not found' };
      }

      return { success: true, data };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Error in getVideoById:', errorMessage);
      return { success: false, error: errorMessage };
    }
  },

  /**
   * Get video by storage path or public URL
   * This is useful when the folder UUID in the path doesn't match the video ID
   */
  getVideoByPath: async (urlOrPath: string): Promise<ServiceResult<Video>> => {
    try {
      // Extract path from URL if it's a full URL
      let path = urlOrPath.trim();
      if (path.startsWith('http')) {
        try {
          const url = new URL(path);
          path = url.pathname.replace(/^\/+/, ''); // Remove leading slashes
        } catch (e) {
          // If URL parsing fails, use the original string
        }
      }

      // Try to find video by public_url first (most reliable)
      const { data: urlData, error: urlError } = await supabase
        .from('videos')
        .select('*')
        .eq('public_url', urlOrPath)
        .eq('is_active', true)
        .maybeSingle();

      if (!urlError && urlData) {
        return { success: true, data: urlData };
      }

      // If not found by URL, try matching storage_path
      // The path might be like "org_xxx/videoUuid/master.mp4" or just "org_xxx/videoUuid/"
      const pathSegments = path.split('/');
      if (pathSegments.length >= 2) {
        // Extract org and video folder: org_xxx/videoUuid/...
        const orgSegment = pathSegments[0];
        const videoFolderSegment = pathSegments[1];
        const expectedPathPrefix = `${orgSegment}/${videoFolderSegment}/`;

        // Query videos where storage_path contains this prefix
        const { data: pathData, error: pathError } = await supabase
          .from('videos')
          .select('*')
          .like('storage_path', `${expectedPathPrefix}%`)
          .eq('is_active', true)
          .limit(1);

        if (!pathError && pathData && pathData.length > 0) {
          return { success: true, data: pathData[0] };
        }
      }

      return { success: false, error: 'Video not found by path or URL' };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Error in getVideoByPath:', errorMessage);
      return { success: false, error: errorMessage };
    }
  },

  /**
   * Create a new video record (provisional, before upload completes)
   */
  createVideo: async (input: CreateVideoInput): Promise<ServiceResult<Video>> => {
    try {
      const { data, error } = await supabase
        .from('videos')
        .insert({
          org_id: input.org_id,
          storage_path: input.storage_path,
          public_url: null,
          source_type: input.source_type,
          file_name: input.file_name,
          description: input.description || null,
          size_bytes: input.size_bytes || null,
          status: 'uploaded',
          uploaded_by: input.uploaded_by,
          is_active: true,
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating video:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Error in createVideo:', errorMessage);
      return { success: false, error: errorMessage };
    }
  },

  /**
   * Update video metadata
   */
  updateVideo: async (
    videoId: string,
    updates: UpdateVideoInput
  ): Promise<ServiceResult<Video>> => {
    try {
      const { data, error } = await supabase
        .from('videos')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', videoId)
        .select()
        .single();

      if (error) {
        console.error('Error updating video:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Error in updateVideo:', errorMessage);
      return { success: false, error: errorMessage };
    }
  },

  /**
   * Soft delete a video
   */
  deleteVideo: async (videoId: string): Promise<ServiceResult<void>> => {
    try {
      const { error } = await supabase
        .from('videos')
        .update({ is_active: false, updated_at: new Date().toISOString() })
        .eq('id', videoId);

      if (error) {
        console.error('Error deleting video:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Error in deleteVideo:', errorMessage);
      return { success: false, error: errorMessage };
    }
  },

  /**
   * Get videos by storage path prefix (for folder browsing)
   */
  getVideosByPathPrefix: async (
    orgId: string,
    pathPrefix: string
  ): Promise<ServiceResult<Video[]>> => {
    try {
      const { data, error } = await supabase
        .from('videos')
        .select('*')
        .eq('org_id', orgId)
        .eq('is_active', true)
        .like('storage_path', `${pathPrefix}%`)
        .order('file_name', { ascending: true });

      if (error) {
        console.error('Error fetching videos by path:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: data || [] };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Error in getVideosByPathPrefix:', errorMessage);
      return { success: false, error: errorMessage };
    }
  },

  /**
   * Check if there are any videos currently being processed (uploaded or processing status)
   * Used to block new uploads until existing ones complete
   */
  hasPendingVideos: async (orgId: string): Promise<ServiceResult<boolean>> => {
    try {
      const { data, error } = await supabase
        .from('videos')
        .select('id')
        .eq('org_id', orgId)
        .eq('is_active', true)
        .in('status', ['uploaded', 'processing'])
        .limit(1);

      if (error) {
        console.error('Error checking pending videos:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: (data?.length ?? 0) > 0 };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Error in hasPendingVideos:', errorMessage);
      return { success: false, error: errorMessage };
    }
  },

  /**
   * Get videos that are stuck in processing/uploaded state for more than specified hours
   * Used to identify and clean up stuck videos
   */
  getStuckVideos: async (
    orgId: string,
    stuckHours: number = 1
  ): Promise<ServiceResult<Video[]>> => {
    try {
      const stuckThreshold = new Date();
      stuckThreshold.setHours(stuckThreshold.getHours() - stuckHours);

      const { data, error } = await supabase
        .from('videos')
        .select('*')
        .eq('org_id', orgId)
        .eq('is_active', true)
        .in('status', ['uploaded', 'processing'])
        .lte('updated_at', stuckThreshold.toISOString())
        .order('updated_at', { ascending: true });

      if (error) {
        console.error('Error fetching stuck videos:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: data || [] };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Error in getStuckVideos:', errorMessage);
      return { success: false, error: errorMessage };
    }
  },

  /**
   * Hard delete a stuck video and its associated processing jobs
   * This permanently removes the video from the database
   */
  deleteStuckVideo: async (videoId: string): Promise<ServiceResult<void>> => {
    try {
      // First, delete associated processing jobs
      const { error: jobsError } = await supabase
        .from('video_processing_jobs')
        .update({ is_active: false })
        .eq('video_id', videoId);

      if (jobsError) {
        console.error('Error deleting processing jobs:', jobsError);
        // Continue with video deletion even if job deletion fails
      }

      // Hard delete the video
      const { error: videoError } = await supabase
        .from('videos')
        .delete()
        .eq('id', videoId);

      if (videoError) {
        console.error('Error deleting video:', videoError);
        return { success: false, error: videoError.message };
      }

      return { success: true };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Error in deleteStuckVideo:', errorMessage);
      return { success: false, error: errorMessage };
    }
  },
};

